import logger from 'logger';
import { panaceaClient } from './client';
import { GIFT_CARD_RANGE } from 'src/constants/gift-cards';
import { dollarsToCents } from 'src/utils/currency';

interface CreateGiftCardSessionResponse {
  clientSecret: string;
}

interface CreateGiftCardSessionInput {
  amountInCents: number;
  type: string;
  returnUrl: string;
  tenantId: number;
}
/**
 *
 * POST `/v1/payments/stripe/create-checkout-session`
 *
 * Creates a checkout session and returns the client secret to be used for an embedded stripe checkout.
 */
export const getStripeClientSecret = async ({
  amountInCents,
  tenantHost,
  tenantId,
}: {
  amountInCents: number;
  tenantHost: string;
  tenantId: number;
}): Promise<string | null> => {
  if (!amountInCents) {
    return null;
  }
  const baseUrl =
    process.env.NODE_ENV === 'development'
      ? 'http://localhost:4003'
      : `https://${tenantHost}`;

  const input: CreateGiftCardSessionInput = {
    amountInCents,
    type: 'GIFT_CARD',
    returnUrl: `${baseUrl}/gift-card/confirmation?session_id={CHECKOUT_SESSION_ID}`,
    tenantId,
  };

  const { data } = await panaceaClient.post<CreateGiftCardSessionResponse>(
    '/v1/payments/stripe/create-checkout-session',
    input,
  );

  if (!data.clientSecret) {
    logger.error({
      msg: 'Failed to create stripe checkout session',
      extra: {
        context: {
          amountInCents,
        },
      },
    });
  }

  return data.clientSecret;
};

/**
 * Validates the gift card amount to ensure it is within the range of 25 <= x <= 500
 */
export const validateGiftCardAmount = (
  amountInCents: number,
): { isValid: boolean } => {
  return {
    isValid:
      amountInCents >= dollarsToCents(GIFT_CARD_RANGE.min) &&
      amountInCents <= dollarsToCents(GIFT_CARD_RANGE.max),
  };
};
